import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RJ9EBA9gxBQOToQ2jjvWVYlI49Lan27m6r6ZykNpyyLRB646t3XryLhxZjTWobF8eEjd0BCd4mYa77Fe5jgZtJl00tAk21aI4');

// Payment Form Component
const PaymentForm = ({ orderData, onPaymentSuccess, onPaymentError, onCancel }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?order_number=${orderData.order_number}&amount=${orderData.total_amount}&currency=${orderData.currency}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setMessage(error.message);
        onPaymentError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded without redirect
        onPaymentSuccess({
          paymentIntent,
          paymentMethod: paymentIntent.payment_method,
          amount: paymentIntent.amount,
        });
      }
    } catch (err) {
      setMessage(t('stripe.unexpectedError'));
      onPaymentError(err);
    }

    setIsLoading(false);
  };

  return (
    <div className="w-full bg-gray-50">
      {/* Payment Form */}
      <form onSubmit={handleSubmit} className="p-0">
        <div className="mb-0">
          <PaymentElement 
            options={{
              layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: false,
                spacedAccordionItems: true,
              },
              paymentMethodOrder: ['bancontact', 'card', 'ideal', 'sepa_debit', 'sofort'],
              fields: {
                billingDetails: {
                  name: 'auto',
                  email: 'auto',
                  phone: 'auto',
                  address: 'auto',
                }
              }
            }}
          />
        </div>

        {message && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mx-4">
            {message}
          </div>
        )}

        <div className="space-y-0 p-4">
          {/* Mobile: Stack buttons vertically, Desktop: Keep horizontal */}
          <div className="flex flex-col md:flex-row gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="w-full md:flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              {t('stripe.cancel')}
            </button>
            
            <button
              type="submit"
              disabled={!stripe || !elements || isLoading}
              className="w-full md:flex-1 px-4 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isLoading ? t('stripe.processing') : t('stripe.payAmount', { amount: (orderData.total_amount).toFixed(2) })}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// Main Stripe Payment Component
const StripePayment = ({ orderData, onPaymentSuccess, onPaymentError, onCancel }) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        console.log('=== StripePayment Debug ===');
        console.log('Order Data:', orderData);
        console.log('User ID in orderData:', orderData.user_id);
        console.log('User Email in orderData:', orderData.user_email);
        console.log('Total Amount:', orderData.total_amount);
        console.log('Amount in euros:', orderData.total_amount);
        
        const paymentData = {
          amount: orderData.total_amount, // Keep as euros - Edge Function will convert to cents
          currency: orderData.currency.toLowerCase(),
          order_id: orderData.order_number,
          metadata: {
            user_id: orderData.user_id,
            user_email: orderData.user_email,
            items: JSON.stringify(orderData.items), // Convert items array to string
            shipping_address: JSON.stringify(orderData.shipping_address), // Convert address to string
            status: orderData.status,
            created_at: orderData.created_at,
            shipping_cost: orderData.shipping_cost || 0,
            discount_amount: orderData.discount_amount || 0,
            
          },
        };
        
        console.log('Payment Data being sent:', paymentData);
        
        const response = await fetch('https://hkdysxiyfpqsnxtexqcm.supabase.co/functions/v1/create-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(paymentData),
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const { client_secret } = await response.json();
        setClientSecret(client_secret);
        setLoading(false);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        setError(t('stripe.initError'));
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, [orderData, t]);

  if (loading) {
    return (
      <div className="w-full bg-gray-50 p-6 md:p-8">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-ambre-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">{t('stripe.initializing')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-gray-50 p-6 md:p-8">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            {t('stripe.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#D97706', // ambre-600
      },
    },
  };

  return (
    <Elements options={options} stripe={stripePromise}>
      <PaymentForm 
        orderData={orderData}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default StripePayment; 